// @ts-nocheck
import React, { useCallback, useMemo } from 'react';
import { useIntl } from 'react-intl';
import { useDispatch, useSelector } from 'react-redux';
import { useReportingMap } from 'sonata-platform-components';
import { Map as MapComponent } from 'sonata-platform-components/lib/types/types/reportingMap.types';
import { BoxV2, Checkbox } from 'sonata-react-components';
import { isDOOHCampaign } from 'sonata-static-storage/lib/helpers/campaign.helpers';
import { checkUserResourcePermissions } from 'sonata-static-storage/lib/helpers/user.helpers';

import {
  reportingMapChangeMapView,
  reportingResetMapData,
  reportingSetCartographyMapConfig
} from '@/store/actions/reporting.actions';
import { setMapType } from '@/store/actions/reportingMap.actions';
import { getCountriesWithEvents, getReportingType, getStaticMode } from '@/store/selectors/reporting.selectors';
import { getUserRoles } from '@/store/selectors/resources.selectors';
import { getCurrentUser } from '@/store/selectors/user.selectors';
import { COUNTRY_LEVEL_CODE } from '@/ts-modules/reporting/Map/helpers';
import { CartographyAdvanceSettings } from './CartographyAdvanceSettings';
import { HeatMapAdvanceSettings } from './HeatMapAdvanceSettings';
import { MapTypeSwitchStyle } from './mapTypeSwitch.style';

type Props = {
  mapId: MapComponent['id'];
  supportedCartographyCountries?: string[];
};

export function MapTypeSwitch({ mapId, supportedCartographyCountries = [] }: Props) {
  const dispatch = useDispatch();
  const { formatMessage } = useIntl();
  const countries = useSelector(getCountriesWithEvents) || [];
  const user = useSelector(getCurrentUser);
  const userRoles = useSelector(getUserRoles);
  const isDooh = isDOOHCampaign(useSelector(getReportingType));
  const staticMode = useSelector(getStaticMode);

  const {
    getMap,
    resetMapCartographyFilter,
    resetMapCartographySettings,
    setMap,
    setMapCartographySettings,
    state: { cartographyStatsAreAvailable }
  } = useReportingMap();
  const map = getMap({ id: mapId })!;

  const { hasCartographyPermissions, hasHeatmapPermissions } = useMemo(
    () => ({
      hasCartographyPermissions: checkUserResourcePermissions('reportingCartographyMap', user, userRoles),
      hasHeatmapPermissions: checkUserResourcePermissions('reportingHeatMap', user, userRoles)
    }),
    [user, userRoles]
  );

  const options = useMemo(
    () =>
      [
        ...(cartographyStatsAreAvailable && supportedCartographyCountries.length ? ['cartography'] : []),
        ...(isDooh ? ['screens'] : []),
        'single',
        'heatmap'
      ].map(mapType => ({
        label: mapType,
        ...(mapType === 'cartography' && {
          additionalSettingsComponents: <CartographyAdvanceSettings mapId={mapId} />
        }),
        ...(mapType === 'heatmap' && {
          additionalSettingsComponents: <HeatMapAdvanceSettings mapId={mapId} />
        }),
        hasPermissions:
          mapType === 'single' ||
          mapType === 'screens' ||
          (mapType === 'heatmap' && hasHeatmapPermissions) ||
          (mapType === 'cartography' && !staticMode && hasCartographyPermissions)
      })),
    [countries, hasHeatmapPermissions, hasCartographyPermissions, isDooh]
  );

  const onClickAction = useCallback(
    option => {
      dispatch(reportingMapChangeMapView('maps', mapId));
      setMap({
        id: mapId,
        value: {
          ...map,
          type: option
        }
      });

      if (option === 'cartography') {
        dispatch(reportingResetMapData(mapId));
      }
      dispatch(setMapType(mapId, option));
      resetMapCartographySettings({ id: mapId });
      resetMapCartographyFilter({ id: mapId });
      if (option === 'cartography') {
        setMapCartographySettings({
          id: mapId,
          value: {
            ...map.settings.cartography,
            countries,
            ...(countries.length > 1 && {
              level: COUNTRY_LEVEL_CODE
            })
          }
        });
        dispatch(
          reportingSetCartographyMapConfig({
            countries,
            ...(countries.length > 1 && {
              level: COUNTRY_LEVEL_CODE
            })
          })
        );
      }
    },
    [map, mapId, setMap]
  );

  return (
    <MapTypeSwitchStyle className="mapInnerControls__section" id={`mapView_${mapId}`}>
      <span className="mapInnerControls__section__description">
        {formatMessage({
          defaultMessage: 'The selected map view will affect the variables settings on the map.',
          id: 'reporting.map.innerControls.mapTypeSwitch.description'
        })}
      </span>
      <div className="mapInnerControls__section__radioButtonGroupWrapper">
        {options
          .filter(option => option.hasPermissions)
          .map(({ additionalSettingsComponents, label: option }) => (
            <BoxV2 borderColor="tableBorderRow" id="mapInnerControls-section-radioButtonOption" key={option}>
              <BoxV2.Body className="mapInnerControls__section__radioButtonOption" padding="8px">
                <div className="mapInnerControls__section__radioButtonOption__header">
                  <Checkbox
                    active={map.type === option}
                    id={`mapView_${mapId}_${option}`}
                    onClickAction={() => onClickAction(option)}
                    type="radio"
                  />
                  <span className="mapInnerControls__section__radioButtonOption__header__title">
                    {formatMessage({
                      defaultMessage: option,
                      id: `reporting.map.innerControls.mapTypeSwitch.${option}OptionTitle`
                    })}
                  </span>
                </div>
                <div className="mapInnerControls__section__radioButtonOption__body">
                  {formatMessage({
                    defaultMessage: `${option} description`,
                    id: `reporting.map.innerControls.mapTypeSwitch.${option}OptionDescription${isDooh ? 'DOOH' : ''}`
                  })}
                </div>
                {option === map.type && additionalSettingsComponents && (
                  <div className="mapInnerControls__section__radioButtonOption__additionalSettings">
                    <div className="mapInnerControls__section__radioButtonOption__additionalSettings__divider" />
                    {additionalSettingsComponents}
                  </div>
                )}
              </BoxV2.Body>
            </BoxV2>
          ))}
      </div>
    </MapTypeSwitchStyle>
  );
}
